/**
 * One meaning per colour, for everything that reports a state.
 *
 * The panel had three palettes running at once for the same idea: project
 * status on antd's 2019 ramp, dashboard KPIs on a Tailwind-family ramp, and
 * task priority on Material 2014. Orange meant "in progress" on a project card
 * and "at risk" on the dashboard, so the colour taught the reader nothing.
 *
 * These values are mirrored by the `--sem-*` custom properties in index.css.
 * Both exist because some consumers cannot use a variable: priority colours are
 * fed to `getContrastColor()` and concatenated with an alpha suffix
 * (`${color}1f`), which needs a real hex. CSS-only consumers should use the
 * tokens; keep the two in step.
 */
export const SEMANTIC = {
  /** Something is already wrong: overdue, failed, blocking. */
  critical: "#dc2626",
  /** Heading for wrong: at risk, behind schedule, high priority. */
  warn: "#ea580c",
  /** Worth noticing, not yet a problem: due soon, normal priority. */
  caution: "#d97706",
  /** Done, healthy, on track. */
  ok: "#16a34a",
  /** No signal: not started, unset, low priority. */
  idle: "#94a3b8",
};

/**
 * Task priority. "In progress" and "high priority" are different kinds of
 * thing, so they no longer share a colour: priority climbs idle → caution →
 * warn → critical, and project state never enters that ramp.
 */
export const priorityOptions = [
  { label: "پایین", value: 1, color: SEMANTIC.idle },
  { label: "متوسط", value: 2, color: SEMANTIC.caution },
  { label: "بالا", value: 3, color: SEMANTIC.warn },
  { label: "بحرانی", value: 4, color: SEMANTIC.critical },
];

/**
 * Project status. Deliberately not on the risk ramp: a project being worked on
 * is not a warning, so "in progress" wears the organization's accent and only
 * genuine trouble — an overdue project — gets a risk colour, applied by the
 * card itself.
 */
export const PROJECT_STATUS = {
  1: { key: "idle", color: SEMANTIC.idle, label: "انجام نشده" },
  2: { key: "accent", color: null, label: "در حال انجام" },
  3: { key: "ok", color: SEMANTIC.ok, label: "انجام شده" },
};

/**
 * Cycle («چرخه») status. Same reading as project status: a cycle being worked
 * on is not a warning, so it wears the accent, and only a cycle that ended with
 * work still open takes a colour off the risk ramp.
 */
export const SPRINT_STATUS = {
  1: { key: "idle", color: SEMANTIC.idle, label: "شروع نشده" },
  2: { key: "accent", color: null, label: "در حال اجرا" },
  3: { key: "ok", color: SEMANTIC.ok, label: "تکمیل شده" },
  4: { key: "warn", color: SEMANTIC.warn, label: "ناتمام" },
};

export const SPRINT_STATUSES = {
  NOT_STARTED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  NOT_COMPLETED: 4,
};

export default SEMANTIC;
